import { CONSOLE_NAME } from '../theme/vocabulary'

// BrandLockup — ATC-110 / `ATC-ADR-018` anchor 8.
//
// The suite mark beside the console's own name. Deduped: `App.tsx`'s sign-in
// card and `Layout.tsx`'s top bar carried byte-identical copies of the text
// lockup, so a change to one silently diverged from the other.
//
// ─────────────────────────────────────────────────────────────────────────────
// "ATC OPERATOR CONSOLE" IS NOT RENAMED TO "FLIGHTDECK".
//
// Style-guide §1.6's wordmark rule (`Flight` extrabold + `Deck` normal, one
// word, capital D) governs THE STRING `FlightDeck`. ATC's product is not
// FlightDeck. The guide's note that "ATC's brand is currently the literal text
// 'ATC Operator Console'" is an observation about the current styling, not an
// instruction to replace the name — an agent reading §1.6 alongside a
// conversion brief will plausibly conclude the opposite, which is why the name
// lives in `theme/vocabulary.ts` behind a test that asserts it does not contain
// "FlightDeck".
//
// The MARK is a different thing from a product name: `flightdeck-mark-dark.svg`
// is the suite mark, and using it here makes ATC's browser tab visually
// identical to the Flight Deck console's. That ambiguity is ACCEPTED
// (`ATC-ADR-018`), following `FD-290`, which records that choosing the light
// R-badge for dark chrome instead is the worse defect.
// ─────────────────────────────────────────────────────────────────────────────
//
// ⚠️ THE VENDORED SVG HAS TWO DEFECTS FOR ATC'S USE, AND BOTH ARE CLOSED BY
// REFERENCING IT AS AN `<img src>` RATHER THAN INLINING IT:
//
//  1. It carries `role="img" aria-label="FlightDeck"`. Inlined, that announces
//     THE WRONG PRODUCT next to the words "ATC Operator Console" — a screen
//     reader would read "FlightDeck, ATC Operator Console". Through `<img>` the
//     host page's own `alt` governs and the SVG's internal label is not
//     exposed; `alt=""` is correct because the adjacent text already names the
//     product, so the mark is decorative here.
//
//  2. `<linearGradient id="rubix">` is a HARDCODED DOM ID. Inlining the file
//     twice — which this lockup does, in the top bar and on the sign-in card —
//     puts two `id="rubix"` nodes in one document; `fill="url(#rubix)"`
//     resolves to the FIRST, so the second mark silently inherits the first's
//     gradient, and duplicate ids are invalid HTML besides. An `<img>` renders
//     each instance in its own document, so the id is scoped per instance and
//     cannot collide. (Inlining would have required namespacing the id per
//     instance instead.)
//
// The file is a BYTE COPY of `flightdeck@…:docs/design/assets/
// flightdeck-mark-dark.svg` (sha256 c80d42ea…). Vendored, not authored — on
// divergence flightdeck wins and ATC re-vendors.

const MARK_SRC = '/flightdeck-mark-dark.svg'

export interface BrandLockupProps {
  /**
   * Which ground the lockup sits on. `dark` is the top bar (`#1B1A19`); `light`
   * is the sign-in card. The mark itself needs no variant — its gradient is
   * drawn to read on both, which is exactly why §1.6 assigns
   * `flightdeck-mark-dark.svg` to dark chrome AND favicons.
   */
  on?: 'dark' | 'light'
}

export function BrandLockup({ on = 'dark' }: BrandLockupProps) {
  // `CONSOLE_NAME` is "ATC Operator Console". Split for the weight treatment
  // only — the string is never altered.
  const [first, ...rest] = CONSOLE_NAME.split(' ')

  return (
    <div className="brand-lockup">
      <img className="brand-mark" src={MARK_SRC} alt="" width={24} height={24} />
      <span className={on === 'dark' ? 'brand brand-dark' : 'brand brand-light'}>
        {first} <span className="brand-sub">{rest.join(' ')}</span>
      </span>
    </div>
  )
}
